<template>
  <div class="order-board">
    <!-- Board Container -->
    <div class="order-board-container">
      <!-- Board Header -->
      <div
        class="board-header bg-white dark:bg-dark-800 px-3 sm:px-6 py-3 sm:py-4 border-b border-surface-200 dark:border-surface-700 transition-colors">
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4">
          <h1 class="text-lg sm:text-xl font-semibold text-gray-800 dark:text-dark-100">Order Board</h1>
          <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 lg:gap-4">
            <!-- Connection Status Indicator -->
            <div
              class="flex items-center bg-surface-50 dark:bg-surface-800 rounded-lg shadow-sm px-2 py-1.5 border border-surface-200 dark:border-surface-700 transition-colors">
              <div class="flex items-center space-x-2">
                <div :class="[
                  'w-2 h-2 rounded-full',
                  realtimeConnection.isConnected.value ? 'bg-green-500' : 'bg-red-500'
                ]"></div>
                <span class="text-xs text-gray-600 dark:text-dark-400">
                  {{ realtimeConnection.isConnected.value ? 'Live' : 'Offline' }}
                </span>
                <button v-if="!realtimeConnection.isConnected.value" @click="realtimeConnection.reconnectAll()"
                  class="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-xs underline ml-1 transition-colors"
                  style="touch-action: manipulation;">
                  Reconnect
                </button>
              </div>
            </div>

            <!-- Search -->
            <div class="relative flex-1 sm:flex-initial">
              <input type="text" v-model="searchQuery" @input="handleSearch" placeholder="Search orders..."
                class="input-field w-full sm:w-48 lg:w-64 pl-10 pr-4 py-2 text-base">
              <span class="absolute left-3 top-2.5 text-gray-400 dark:text-dark-400">
                <i class="fas fa-search"></i>
              </span>
            </div>

            <!-- Mobile: Collapsible Controls -->
            <div class="flex items-center gap-2 sm:gap-3">
              <!-- View Toggle Button -->
              <button @click="toggleView"
                class="btn bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300 hover:bg-surface-300 dark:hover:bg-surface-600 flex items-center justify-center px-3 sm:px-4 py-2 min-h-[44px]"
                style="touch-action: manipulation;" :title="isCompactView ? 'Expand View' : 'Compact View'">
                <i :class="['fas', isCompactView ? 'fa-expand' : 'fa-compress']"></i>
                <span class="hidden sm:inline ml-2">{{ isCompactView ? 'Expand' : 'Compact' }}</span>
              </button>

              <!-- Sort Button -->
              <button @click="toggleSort"
                class="btn bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300 hover:bg-surface-300 dark:hover:bg-surface-600 flex items-center justify-center px-3 sm:px-4 py-2 min-h-[44px]"
                style="touch-action: manipulation;" title="Sort by Date">
                <i class="fas fa-sort"></i>
                <span class="hidden lg:inline ml-2">Sort by Date</span>
                <span v-if="sortDirection"
                  class="ml-2 px-2 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-full">
                  <span class="hidden sm:inline">{{ sortDirection === 'asc' ? 'Oldest' : 'Newest' }}</span>
                  <i :class="sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'" class="ml-1"></i>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Board Content -->
      <div class="board-content">
        <!-- Mobile: Horizontally scrollable columns -->
        <div class="board-columns-mobile lg:hidden">
          <div class="board-columns-scroll">
            <template v-for="column in boardColumns" :key="column.status + (column.subStatus || '')">
              <div class="board-column-mobile">
                <div class="column-header">
                  <div class="flex items-center">
                    <span :class="['status-dot', `bg-${column.dotColor}`]"></span>
                    <h3>{{ column.label }}</h3>
                    <span class="count-badge">{{ getTotalOrders(column) }}</span>
                  </div>
                </div>
                <div class="column-content">
                  <div class="orders-list">
                    <div class="orders-container">
                      <OrderCard v-for="order in column.getOrders()" :key="order.id" :order="order"
                        :is-compact="isCompactView" @move="showMoveConfirmation" @preview="showOrderPreviewModal" />
                    </div>

                    <!-- Load More Button -->
                    <div v-if="canLoadMore(column)" class="load-more-container">
                      <button @click="() => loadMoreOrders(column)" class="load-more-btn"
                        :disabled="orderStore.loading">
                        <template v-if="orderStore.loading">
                          <i class="fas fa-spinner fa-spin mr-2"></i>
                          Loading...
                        </template>
                        <template v-else>
                          <i class="fas fa-plus mr-2"></i>
                          Load More ({{ getColumnOrderCount(column) }}/{{ getTotalOrders(column) }})
                        </template>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Desktop: Grid layout -->
        <div class="board-columns hidden lg:grid">
          <template v-for="column in boardColumns" :key="column.status + (column.subStatus || '')">
            <div class="board-column">
              <div class="column-header">
                <div class="flex items-center">
                  <span :class="['status-dot', `bg-${column.dotColor}`]"></span>
                  <h3>{{ column.label }}</h3>
                  <span class="count-badge">{{ getTotalOrders(column) }}</span>
                </div>
              </div>
              <div class="column-content">
                <div class="orders-list">
                  <div class="orders-container">
                    <OrderCard v-for="order in column.getOrders()" :key="order.id" :order="order"
                      :is-compact="isCompactView" @move="showMoveConfirmation" @preview="showOrderPreviewModal" />
                  </div>

                  <!-- Load More Button -->
                  <div v-if="canLoadMore(column)" class="load-more-container">
                    <button @click="() => loadMoreOrders(column)" class="load-more-btn" :disabled="orderStore.loading">
                      <template v-if="orderStore.loading">
                        <i class="fas fa-spinner fa-spin mr-2"></i>
                        Loading...
                      </template>
                      <template v-else>
                        <i class="fas fa-plus mr-2"></i>
                        Load More ({{ getColumnOrderCount(column) }}/{{ getTotalOrders(column) }})
                      </template>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Move Confirmation Modal -->
      <TransitionRoot appear :show="modalState.showConfirmation" as="template">
        <Dialog as="div" @close="closeMoveConfirmationModal" class="relative z-50">
          <TransitionChild enter="duration-300 ease-out" enter-from="opacity-0" enter-to="opacity-100"
            leave="duration-200 ease-in" leave-from="opacity-100" leave-to="opacity-0">
            <div class="fixed inset-0 bg-black/30" />
          </TransitionChild>

          <div class="fixed inset-0 overflow-y-auto">
            <div class="flex min-h-full items-center justify-center p-4">
              <TransitionChild enter="duration-300 ease-out" enter-from="opacity-0 scale-95"
                enter-to="opacity-100 scale-100" leave="duration-200 ease-in" leave-from="opacity-100 scale-100"
                leave-to="opacity-0 scale-95">
                <DialogPanel
                  class="bg-white dark:bg-dark-800 w-full max-w-md transform overflow-hidden rounded-lg border border-gray-200 dark:border-dark-700 p-4 sm:p-6 shadow-xl transition-all mx-4 sm:mx-0">
                  <DialogTitle class="text-lg font-medium text-gray-900 dark:text-dark-100 mb-4">
                    Confirm Status Update
                  </DialogTitle>

                  <div class="space-y-4">
                    <!-- Status Change Information -->
                    <div class="bg-surface-100 dark:bg-surface-800 p-4 rounded-lg">
                      <p class="text-sm text-gray-600 dark:text-dark-400">Are you sure you want to update the status?
                      </p>
                      <div class="mt-2 flex items-center gap-2">
                        <span class="text-gray-700 dark:text-dark-200 font-medium">
                          {{ modalState.selectedOrder?.status === 'production'
                            ? modalState.selectedOrder?.sub_status
                            : modalState.selectedOrder?.status }}
                        </span>
                        <i class="fas fa-arrow-right text-gray-400 dark:text-dark-400"></i>
                        <span class="text-primary-600 dark:text-primary-400 font-medium">
                          {{ modalState.nextStatus?.status === 'production'
                            ? modalState.nextStatus?.subStatus
                            : modalState.nextStatus?.status }}
                        </span>
                      </div>
                    </div>

                    <!-- Image Upload Section - Only show if required -->
                    <div v-if="requiresImage" class="space-y-4">
                      <div class="border-t border-gray-200 my-4"></div>

                      <p class="text-sm text-gray-600 dark:text-dark-400">
                        Please upload an image to proceed with the status update.
                      </p>

                      <!-- Image Upload Input -->
                      <div class="mt-4">
                        <div
                          class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-dark-600 border-dashed rounded-md hover:border-blue-500 transition-colors cursor-pointer"
                          :class="{ 'border-blue-500 bg-blue-50 dark:bg-blue-900/20': dragover }"
                          @dragover.prevent="dragover = true" @dragleave.prevent="dragover = false"
                          @drop.prevent="handleDrop">
                          <div class="space-y-1 text-center">
                            <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-dark-400" stroke="currentColor"
                              fill="none" viewBox="0 0 48 48" aria-hidden="true">
                              <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                            <div class="flex text-sm text-gray-600 dark:text-dark-400">
                              <label
                                class="relative cursor-pointer bg-white dark:bg-dark-700 rounded-md font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 focus-within:outline-none transition-colors">
                                <span>Upload a file</span>
                                <input type="file" class="sr-only" accept="image/*" @change="handleImageSelect">
                              </label>
                              <p class="pl-1">or drag and drop</p>
                            </div>
                            <p class="text-xs text-gray-500 dark:text-dark-400">
                              PNG, JPG, GIF up to 10MB
                            </p>
                          </div>
                        </div>
                      </div>

                      <!-- Preview Image -->
                      <div v-if="imagePreviewUrl" class="mt-4">
                        <div class="relative">
                          <img :src="imagePreviewUrl" alt="Preview" class="max-h-48 mx-auto rounded-lg">
                          <button @click="modalState.selectedImage = null"
                            class="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors">
                            <i class="fas fa-times"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="mt-6 flex flex-col sm:flex-row justify-end gap-3">
                    <button type="button" @click="closeMoveConfirmationModal"
                      class="btn bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300 hover:bg-surface-300 dark:hover:bg-surface-600 w-full sm:w-auto px-4 py-2.5 text-sm font-medium rounded-md transition-colors min-h-[44px]"
                      style="touch-action: manipulation;" :disabled="modalState.isMoving">
                      Cancel
                    </button>
                    <button type="button" @click="handleMoveConfirmation"
                      :disabled="modalState.isMoving || (requiresImage && !modalState.selectedImage)"
                      class="btn btn-primary w-full sm:w-auto px-4 py-2.5 text-sm font-medium border-transparent rounded-md disabled:opacity-50 transition-colors min-h-[44px]"
                      style="touch-action: manipulation;">
                      <template v-if="modalState.isMoving">
                        <i class="fas fa-spinner fa-spin mr-2"></i>
                        Updating...
                      </template>
                      <template v-else>
                        Confirm Update
                      </template>
                    </button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </TransitionRoot>

      <!-- Order Preview Modal -->
      <TransitionRoot appear :show="showOrderPreview" as="template">
        <Dialog as="div" @close="closeOrderPreviewModal" class="relative z-50">
          <TransitionChild enter="duration-300 ease-out" enter-from="opacity-0" enter-to="opacity-100"
            leave="duration-200 ease-in" leave-from="opacity-100" leave-to="opacity-0">
            <div class="fixed inset-0 bg-black/30" />
          </TransitionChild>

          <div class="fixed inset-0 overflow-y-auto">
            <div class="flex min-h-full items-center justify-center p-0 sm:p-4">
              <TransitionChild enter="duration-300 ease-out" enter-from="opacity-0 scale-95"
                enter-to="opacity-100 scale-100" leave="duration-200 ease-in" leave-from="opacity-100 scale-100"
                leave-to="opacity-0 scale-95">
                <DialogPanel
                  class="bg-white dark:bg-dark-800 w-full max-w-6xl transform overflow-hidden rounded-none sm:rounded-lg border border-gray-200 dark:border-dark-700 shadow-xl transition-all h-full sm:h-auto max-h-[100vh] sm:max-h-[90vh] flex flex-col">
                  <div
                    class="px-4 sm:px-6 py-3 sm:py-4 border-b border-surface-200 dark:border-surface-700 flex-shrink-0">
                    <div class="flex items-center justify-between">
                      <DialogTitle class="text-base sm:text-lg font-medium text-gray-900 dark:text-dark-100">
                        Order Details
                      </DialogTitle>
                      <button @click="closeOrderPreviewModal"
                        class="text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 transition-colors p-2 -mr-2"
                        style="touch-action: manipulation;">
                        <i class="fas fa-times text-xl"></i>
                      </button>
                    </div>
                  </div>

                  <div class="flex-1 overflow-y-auto max-h-[calc(100vh-80px)] sm:max-h-[80vh]">
                    <OrderPreview v-if="selectedOrderId" :order-id="selectedOrderId" />
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </TransitionRoot>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, onUnmounted, onBeforeUnmount, watch } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue';
import { useOrderStore } from '@/stores/orderStore';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/composables/useToast';
import { useRealtimeConnection } from '@/composables/useRealtimeConnection';
import OrderCard from '@/components/OrderCard.vue';
import OrderPreview from '@/components/orders/OrderPreview.vue';
import { debounce } from 'lodash-es';
import { ordersApi, api } from '@/services/api';

// Store
const orderStore = useOrderStore();
const authStore = useAuthStore();
const toast = useToast();
const realtimeConnection = useRealtimeConnection();

// Data
const searchQuery = ref('');
const sortDirection = ref(null); // null, 'asc', or 'desc'
const isCompactView = ref(true);
const ordersInProcess = ref(new Set());

// Fallback polling mechanism - reduced frequency to minimize database load
let fallbackPollingInterval = null;
const FALLBACK_POLLING_INTERVAL = 300000; // 5 minutes (reduced from 1 minute)

// Production sub-statuses
const productionSubStatuses = [
  { value: 'layouting', label: 'Layouting' },
  { value: 'printing', label: 'Printing' },
  { value: 'operations', label: 'Operations' }
];

// Sort Data - Simple toggle between ASC/DESC for created_at 

const boardColumns = computed(() => [
  {
    status: 'in_review',
    label: 'In Review',
    dotColor: 'yellow-400',
    getOrders: () => getColumnOrders({ status: 'in_review' })
  },
  {
    status: 'production',
    subStatus: 'layouting',
    label: 'Layouting',
    dotColor: 'blue-400',
    getOrders: () => getColumnOrders({ status: 'production', subStatus: 'layouting' })
  },
  {
    status: 'production',
    subStatus: 'printing',
    label: 'Printing',
    dotColor: 'blue-400',
    getOrders: () => getColumnOrders({ status: 'production', subStatus: 'printing' })
  },
  {
    status: 'production',
    subStatus: 'operations',
    label: 'Operations',
    dotColor: 'blue-400',
    getOrders: () => getColumnOrders({ status: 'production', subStatus: 'operations' })
  },
  {
    status: 'quality_check',
    label: 'Quality Check',
    dotColor: 'purple-400',
    getOrders: () => getColumnOrders({ status: 'quality_check' })
  },
  {
    status: 'ready_for_release',
    label: 'Ready for Release',
    dotColor: 'green-400',
    getOrders: () => getColumnOrders({ status: 'ready_for_release' })
  },
  {
    status: 'released',
    label: 'Released',
    dotColor: 'emerald-500',
    getOrders: () => getColumnOrders({ status: 'released' })
  }
]);

// Add computed property for requiresImage
const requiresImage = computed(() => {
  const order = modalState.value.selectedOrder;
  const nextStatus = modalState.value.nextStatus;

  if (!order || !nextStatus) return false;

  return (
    (order.status === 'production' && order.sub_status === 'layouting' && nextStatus.status === 'production' && nextStatus.subStatus === 'printing') ||
    (order.status === 'quality_check' && nextStatus.status === 'ready_for_release')
  );
});

// Add this computed property after other computed properties
const imagePreviewUrl = computed(() => {
  if (!modalState.value.selectedImage) return null;
  return window.URL.createObjectURL(modalState.value.selectedImage);
});

// Add order preview modal state
const showOrderPreview = ref(false);
const selectedOrderId = ref(null);

// Helper function to get full R2 image URL with fallback
const getFullImageUrl = (url) => {
  if (!url) return '';

  console.log('[OrderBoard] Processing image URL:', url);

  // If it's already an absolute URL, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    console.log('[OrderBoard] Using full URL directly:', url);
    return url;
  }

  // Get R2 URL from environment variables
  const r2PublicUrl = import.meta.env.VITE_R2_PUBLIC_URL || 'https://pub-5d1a7afaa6c74e6985f5d6263b64da87.r2.dev';

  // Clean up the path (remove any leading slashes)
  const cleanPath = url.replace(/^\/+/, '');

  // Check if path appears to be a storage path or contains "general"
  if (cleanPath.includes('/') || cleanPath.includes('general')) {
    // Use R2 for storage paths
    const fullUrl = `${r2PublicUrl}/${cleanPath}`;
    console.log('[OrderBoard] Using R2 URL:', fullUrl);
    return fullUrl;
  }

  // For other paths, use the API URL
  const apiUrl = import.meta.env.VITE_API_URL;
  const fullUrl = `${apiUrl}/${cleanPath}`;
  console.log('[OrderBoard] Using API URL:', fullUrl);
  return fullUrl;
};

// Methods
const getColumnOrders = (column) => {
  const statusKey = column.subStatus || 'default';
  return orderStore.ordersByStatus[column.status]?.[statusKey]?.orders || [];
};

const handleSearch = debounce(async () => {
  try {
    // console.log('Searching orders:', searchQuery.value);

    // Build sort parameters if sorting is active
    const sortParams = sortDirection.value ? {
      field: 'created_at',
      direction: sortDirection.value
    } : null;

    // Load all columns in parallel with a single request per status
    const loadingPromises = new Set();

    boardColumns.value.forEach(column => {
      const key = `${column.status}-${column.subStatus || 'default'}`;
      if (!loadingPromises.has(key)) {
        loadingPromises.add(key);
        orderStore.fetchBoardOrders(
          column.status,
          column.subStatus,
          1,
          searchQuery.value,
          null, // filters
          sortParams // sort params
        );
      }
    });

    await Promise.all([...loadingPromises]);
  } catch (error) {
    console.error('Error searching orders:', error);
    toast.error('Failed to search orders');
  }
}, 500);

const toggleSort = async () => {
  try {
    // Toggle sort direction: null -> desc -> asc -> null
    if (sortDirection.value === null) {
      sortDirection.value = 'desc'; // Start with newest first
    } else if (sortDirection.value === 'desc') {
      sortDirection.value = 'asc'; // Switch to oldest first
    } else {
      sortDirection.value = null; // Clear sorting
    }

    // Build sort parameters
    const sortParams = sortDirection.value ? {
      field: 'created_at',
      direction: sortDirection.value
    } : null;

    // Apply sorting to each column
    const loadingPromises = new Set();

    boardColumns.value.forEach(column => {
      const key = `${column.status}-${column.subStatus || 'default'}`;
      if (!loadingPromises.has(key)) {
        loadingPromises.add(key);
        orderStore.fetchBoardOrders(
          column.status,
          column.subStatus,
          1,
          searchQuery.value,
          null, // filters
          sortParams // sort params
        );
      }
    });

    await Promise.all([...loadingPromises]);

    // Show success message
    if (sortDirection.value) {
      const message = sortDirection.value === 'desc' ? 'Newest First' : 'Oldest First';
      //toast.success(`Orders sorted by Created Date (${message})`);
    } else {
      //toast.success('Sorting cleared');
    }
  } catch (error) {
    console.error('Error toggling sort:', error);
    toast.error('Failed to apply sorting');
  }
};

// Move Modal State
const modalState = ref({
  showConfirmation: false,
  selectedOrder: null,
  nextStatus: null,
  isMoving: false,
  showImageUpload: false,
  selectedImage: null
});

const showMoveConfirmation = async (order) => {
  try {
    console.log('Moving order:', order);

    // Check if user has permission to move this order
    const permissionCheck = await ordersApi.checkStatusChangePermission(order.id);

    if (!permissionCheck.canChange) {
      toast.error('You do not have permission to update this order status.');
      return;
    }

    // Get next status from the permission check
    const next = permissionCheck.nextStatus;
    console.log('Next status:', next);

    if (!next) {
      toast.error('Invalid status transition');
      return;
    }

    // Set the modal state in a single update
    modalState.value = {
      showConfirmation: true,
      selectedOrder: {
        ...order,
        sub_status: order.sub_status || (order.status === 'production' ? 'layouting' : null)
      },
      nextStatus: { ...next },
      isMoving: false,
      showImageUpload: false,
      selectedImage: null
    };

    // Add to ordersInProcess
    ordersInProcess.value.add(order.id);

  } catch (error) {
    console.error('Error in showMoveConfirmation:', error);
    toast.error('Error preparing status update');
  }
};

const handleMoveConfirmation = async () => {
  console.log('=== Starting handleMoveConfirmation ===');
  console.log('Modal State:', modalState.value);

  const { selectedOrder, nextStatus } = modalState.value;

  console.log('Extracted values:', {
    selectedOrder: {
      id: selectedOrder?.id,
      status: selectedOrder?.status,
      sub_status: selectedOrder?.sub_status
    },
    nextStatus: {
      status: nextStatus?.status,
      subStatus: nextStatus?.subStatus
    }
  });

  if (!selectedOrder || !nextStatus) {
    console.error('Missing required data for move:', {
      hasSelectedOrder: !!selectedOrder,
      hasNextStatus: !!nextStatus
    });
    return;
  }

  try {
    modalState.value.isMoving = true;

    console.log('Creating FormData with:', {
      currentStatus: selectedOrder.status,
      currentSubStatus: selectedOrder.sub_status,
      nextStatus: nextStatus.status,
      nextSubStatus: nextStatus.subStatus,
      requiresImage: requiresImage.value,
      hasImage: !!modalState.value.selectedImage
    });

    // Create FormData and append all required fields
    let formData = new FormData();

    // Always append status and sub_status
    formData.append('status', nextStatus.status);
    formData.append('sub_status', nextStatus.subStatus || '');
    formData.append('changed_by', authStore.user.id);

    console.log('FormData contents:', {
      status: nextStatus.status,
      sub_status: nextStatus.subStatus || '',
      changed_by: authStore.user.id
    });

    // Upload image to R2 if we have one
    if (modalState.value.selectedImage) {
      console.log('[OrderBoard] Status change with image, using direct R2 upload');

      try {
        // First, upload the image directly to R2
        const uploadFormData = new FormData();
        uploadFormData.append('image', modalState.value.selectedImage);

        // Determine the appropriate R2 path based on the status transition
        let uploadPath = 'orders/production'; // default path
        if (selectedOrder.status === 'production' && selectedOrder.sub_status === 'layouting' &&
          nextStatus.status === 'production' && nextStatus.subStatus === 'printing') {
          uploadPath = 'orders/production';
        } else if (selectedOrder.status === 'quality_check' && nextStatus.status === 'ready_for_release') {
          uploadPath = 'orders/quality_check';
        }

        uploadFormData.append('path', uploadPath);  // Specify R2 path

        // Upload image first
        const uploadResponse = await api.post('/upload', uploadFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        console.log('[OrderBoard] Image uploaded to R2:', uploadResponse.data);

        // If image upload was successful, pass the image URL to status update
        if (uploadResponse.data && uploadResponse.data.url) {
          // Add the R2 image URL to the status update form using standard image_url field
          formData.append('image_url', uploadResponse.data.url);
          console.log('[OrderBoard] Using R2 image URL:', uploadResponse.data.url);
        }
      } catch (uploadError) {
        console.error('[OrderBoard] Error uploading image to R2:', uploadError);
        toast.error('Failed to upload image. Please try again.');
        return; // Stop execution - don't proceed without required image
      }
    }

    console.log('Calling updateOrderStatus with:', {
      orderId: selectedOrder.id,
      status: nextStatus.status,
      subStatus: nextStatus.subStatus || '',
      hasFormData: true
    });

    // Update order through store
    await orderStore.updateOrderStatus({
      orderId: selectedOrder.id,
      status: nextStatus.status,
      subStatus: nextStatus.subStatus || '',
      updatedBy: authStore.user.id,
      formData: formData
    });

    console.log('Status update successful');
    toast.success('Order status updated successfully');

    // Refresh both the source and target columns
    console.log('Refreshing columns:', {
      source: { status: selectedOrder.status, subStatus: selectedOrder.sub_status },
      target: { status: nextStatus.status, subStatus: nextStatus.subStatus }
    });

    await Promise.all([
      orderStore.fetchBoardOrders(selectedOrder.status, selectedOrder.sub_status),
      orderStore.fetchBoardOrders(nextStatus.status, nextStatus.subStatus)
    ]);

  } catch (error) {
    console.error('Error moving order:', error);

    // Check if it's a permission error
    if (error.response?.status === 403) {
      toast.error('You do not have permission to perform this status change.');
    } else {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        config: error.config
      });
      toast.error('Failed to update order status. Please try again.');
    }
  } finally {
    console.log('=== Ending handleMoveConfirmation ===');
    cleanupStates();
  }
};

const toggleView = () => {
  isCompactView.value = !isCompactView.value;
};

// Initialize data
let refreshInterval;

onMounted(async () => {
  try {
    console.log('Initializing order board...');

    // Initialize robust realtime connection monitoring
    realtimeConnection.initialize();

    // Create realtime subscription with automatic reconnection
    const handleOrdersChange = (payload) => {
      console.log('[OrderBoard] Order table changed:', payload);

      // Handle the change based on the event type
      if (payload.eventType === 'INSERT') {
        handleOrderInsert(payload.new);
      } else if (payload.eventType === 'UPDATE') {
        handleOrderUpdate(payload.new, payload.old);
      } else if (payload.eventType === 'DELETE') {
        handleOrderDelete(payload.old);
      }
    };

    // Set up subscription with automatic reconnection
    await realtimeConnection.createSubscription(
      'order-board-changes',
      { event: '*', schema: 'public', table: 'orders' },
      handleOrdersChange
    );

    // Set up fallback polling when realtime is disconnected
    const startFallbackPolling = () => {
      if (fallbackPollingInterval) return;

      console.log('[OrderBoard] Starting fallback polling');
      fallbackPollingInterval = setInterval(() => {
        if (!realtimeConnection.isConnected.value && realtimeConnection.isTabVisible.value) {
          console.log('[OrderBoard] Fallback polling - refreshing data');
          const refreshPromises = new Set();

          boardColumns.value.forEach(column => {
            const key = `${column.status}-${column.subStatus || 'default'}`;
            if (!refreshPromises.has(key)) {
              refreshPromises.add(key);
              orderStore.fetchBoardOrders(
                column.status,
                column.subStatus,
                1,
                searchQuery.value
              );
            }
          });
        }
      }, FALLBACK_POLLING_INTERVAL);
    };

    const stopFallbackPolling = () => {
      if (fallbackPollingInterval) {
        clearInterval(fallbackPollingInterval);
        fallbackPollingInterval = null;
        console.log('[OrderBoard] Stopped fallback polling');
      }
    };

    // Watch connection state to enable/disable fallback polling
    watch(() => realtimeConnection.isConnected.value, (isConnected) => {
      if (isConnected) {
        stopFallbackPolling();
      } else {
        startFallbackPolling();
      }
    });

    // Preload user data for better performance
    await orderStore.preloadUserData();

    // Load all columns in parallel with a single request per status
    const loadingPromises = new Set();

    boardColumns.value.forEach(column => {
      const key = `${column.status}-${column.subStatus || 'default'}`;
      if (!loadingPromises.has(key)) {
        loadingPromises.add(key);
        orderStore.fetchBoardOrders(
          column.status,
          column.subStatus,
          1,
          searchQuery.value
        );
      }
    });

    await Promise.all([...loadingPromises]);
    console.log('Initial board data loaded and real-time updates initialized');

    // Start fallback polling if realtime is not connected
    if (!realtimeConnection.isConnected.value) {
      startFallbackPolling();
    }

    // Set up a fallback refresh every 5 minutes as a backup (extended from 5 seconds for efficiency)
    refreshInterval = setInterval(async () => {
      console.log('Running fallback refresh (should be rare if realtime is working)');
      const refreshPromises = new Set();

      boardColumns.value.forEach(column => {
        const key = `${column.status}-${column.subStatus || 'default'}`;
        if (!refreshPromises.has(key)) {
          refreshPromises.add(key);

          // Get current page from pagination data
          const currentData = orderStore.ordersByStatus[column.status]?.[key];
          const currentPage = currentData?.pagination?.current_page || 1;

          // Skip refresh for columns that are actively loading more items
          const loadingKey = `${column.status}-${key}-${currentPage}`;
          if (orderStore.currentLoadingKey === loadingKey) {
            console.log(`Skipping refresh for ${column.label} as it's actively loading`);
            return;
          }

          // Only refresh if we're on page 1 or if this is not a paginated view
          if (currentPage === 1) {
            orderStore.fetchBoardOrders(
              column.status,
              column.subStatus,
              1,  // Always refresh first page only
              searchQuery.value
            );
          }
        }
      });

      await Promise.all([...refreshPromises]);
    }, 300000); // Increased to 5 minutes because realtime should handle most updates
  } catch (error) {
    console.error('Error loading initial data:', error);
    toast.error('Failed to load orders');
  }
});

// Handler for INSERT event
const handleOrderInsert = (newOrder) => {
  console.log('[Realtime] Processing new order:', newOrder);

  // Determine which column this order belongs to
  const status = newOrder.status;
  const subStatus = newOrder.sub_status || 'default';

  // Check if this new order should be displayed in our board
  if (boardColumns.value.some(col =>
    col.status === status &&
    (col.subStatus === subStatus || (!col.subStatus && subStatus === 'default'))
  )) {
    // Add visual indicator for new order
    newOrder.isHighlighted = true;

    // Refresh the specific column to include the new order
    orderStore.fetchBoardOrders(status, subStatus === 'default' ? null : subStatus, 1, searchQuery.value);

    // Remove highlight after 5 seconds
    setTimeout(() => {
      // Find the order in our state and remove highlight
      const column = orderStore.ordersByStatus[status];
      const orderList = column?.[subStatus]?.orders;

      if (orderList) {
        const index = orderList.findIndex(o => o.id === newOrder.id);
        if (index !== -1) {
          orderList[index].isHighlighted = false;
        }
      }
    }, 5000);
  }
};

// Handler for UPDATE event
const handleOrderUpdate = (newOrder, oldOrder) => {
  console.log('[Realtime] Processing order update:', { old: oldOrder, new: newOrder });

  // Check if status or sub_status has changed
  const statusChanged = newOrder.status !== oldOrder.status || newOrder.sub_status !== oldOrder.sub_status;

  if (statusChanged) {
    // Remove from old column and add to new column
    const oldStatus = oldOrder.status;
    const oldSubStatus = oldOrder.sub_status || 'default';
    const newStatus = newOrder.status;
    const newSubStatus = newOrder.sub_status || 'default';

    // Refresh both columns
    if (boardColumns.value.some(col =>
      col.status === oldStatus &&
      (col.subStatus === oldSubStatus || (!col.subStatus && oldSubStatus === 'default'))
    )) {
      orderStore.fetchBoardOrders(oldStatus, oldSubStatus === 'default' ? null : oldSubStatus, 1, searchQuery.value);
    }

    if (boardColumns.value.some(col =>
      col.status === newStatus &&
      (col.subStatus === newSubStatus || (!col.subStatus && newSubStatus === 'default'))
    )) {
      orderStore.fetchBoardOrders(newStatus, newSubStatus === 'default' ? null : newSubStatus, 1, searchQuery.value);
    }
  } else {
    // Just an update to order properties, refresh the current column
    const status = newOrder.status;
    const subStatus = newOrder.sub_status || 'default';

    if (boardColumns.value.some(col =>
      col.status === status &&
      (col.subStatus === subStatus || (!col.subStatus && subStatus === 'default'))
    )) {
      // Visual indicator for updated order
      const column = orderStore.ordersByStatus[status];
      const orderList = column?.[subStatus]?.orders;

      if (orderList) {
        const index = orderList.findIndex(o => o.id === newOrder.id);
        if (index !== -1) {
          // Update in place without full refresh for better performance
          orderList[index] = { ...orderList[index], ...newOrder, isHighlighted: true };

          // Remove highlight after 5 seconds
          setTimeout(() => {
            const updatedIndex = orderList.findIndex(o => o.id === newOrder.id);
            if (updatedIndex !== -1) {
              orderList[updatedIndex].isHighlighted = false;
            }
          }, 5000);
        }
      }
    }
  }
};

// Handler for DELETE event
const handleOrderDelete = (deletedOrder) => {
  console.log('[Realtime] Processing order deletion:', deletedOrder);

  // Determine which column this order was in
  const status = deletedOrder.status;
  const subStatus = deletedOrder.sub_status || 'default';

  // Refresh the column to remove the deleted order
  if (boardColumns.value.some(col =>
    col.status === status &&
    (col.subStatus === subStatus || (!col.subStatus && subStatus === 'default'))
  )) {
    orderStore.fetchBoardOrders(status, subStatus === 'default' ? null : subStatus, 1, searchQuery.value);
  }
};

// Clean up subscriptions and intervals
onBeforeUnmount(() => {
  // Stop fallback polling
  if (fallbackPollingInterval) {
    clearInterval(fallbackPollingInterval);
    fallbackPollingInterval = null;
  }

  // Cleanup is handled automatically by the useRealtimeConnection composable
  console.log('[OrderBoard] Component unmounting - realtime connections will be cleaned up automatically');
});

// Clean up the interval when the component is unmounted
onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
});

const loadMoreOrders = async (column) => {
  try {
    const statusKey = column.subStatus || 'default';
    const columnData = orderStore.ordersByStatus[column.status]?.[statusKey];

    if (!columnData?.pagination) {
      console.log('No pagination data found for column:', column);
      return;
    }

    const { current_page, total_pages, total } = columnData.pagination;
    const currentCount = columnData.orders?.length || 0;

    // Check if we can load more
    if (currentCount >= total || current_page >= total_pages) {
      console.log('No more orders to load');
      return;
    }

    // Check if we're already loading
    if (orderStore.loading) {
      console.log('Already loading orders, please wait...');
      return;
    }

    console.log(`Loading more orders for ${column.label} (page ${current_page + 1})`);
    await orderStore.fetchBoardOrders(
      column.status,
      column.subStatus,
      current_page + 1,
      searchQuery.value
    );
  } catch (error) {
    console.error('Error loading more orders:', error);
    toast.error('Failed to load more orders');
  }
};

const canLoadMore = (column) => {
  const statusKey = column.subStatus || 'default';
  const columnData = orderStore.ordersByStatus[column.status]?.[statusKey];

  if (!columnData?.pagination) {
    return false;
  }

  const { current_page, total_pages, total } = columnData.pagination;
  const currentCount = columnData.orders?.length || 0;

  // Return false if we're loading or if we've reached the total
  if (orderStore.loading || currentCount >= total || current_page >= total_pages) {
    return false;
  }

  return total > currentCount;
};

const getColumnOrderCount = (column) => {
  const statusKey = column.subStatus || 'default';
  const columnData = orderStore.ordersByStatus[column.status]?.[statusKey];
  return columnData?.orders?.length || 0;
};

const getTotalOrders = (column) => {
  const statusKey = column.subStatus || 'default';
  const columnData = orderStore.ordersByStatus[column.status]?.[statusKey];

  // If no pagination data, use the current orders length
  if (!columnData?.pagination?.total) {
    const currentCount = columnData?.orders?.length || 0;
    // console.log('Using current count for column:', {
    //   column: column.label,
    //   count: currentCount
    // });
    return currentCount;
  }

  // console.log('Total orders for column:', {
  //   column: column.label,
  //   total: columnData.pagination.total
  // });

  return columnData.pagination.total;
};

// Update cleanupStates function
const cleanupStates = () => {
  if (imagePreviewUrl.value) {
    window.URL.revokeObjectURL(imagePreviewUrl.value);
  }
  modalState.value = {
    showConfirmation: false,
    selectedOrder: null,
    nextStatus: null,
    isMoving: false,
    selectedImage: null
  };
  ordersInProcess.value.clear();
};

// Order Preview Modal Functions
const showOrderPreviewModal = (order) => {
  selectedOrderId.value = order.id;
  showOrderPreview.value = true;
};

const closeOrderPreviewModal = () => {
  showOrderPreview.value = false;
  selectedOrderId.value = null;
};

// Update closeMoveConfirmationModal
const closeMoveConfirmationModal = () => {
  cleanupStates();
};

// Add drag and drop state
const dragover = ref(false);

// Update handleDrop function
const handleDrop = (event) => {
  dragover.value = false;
  const file = event.dataTransfer.files[0];
  if (file && file.type.startsWith('image/')) {
    if (imagePreviewUrl.value) {
      window.URL.revokeObjectURL(imagePreviewUrl.value);
    }
    modalState.value.selectedImage = file;
  } else {
    toast.error('Please upload an image file');
  }
};

// Update handleImageSelect function
const handleImageSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    if (imagePreviewUrl.value) {
      window.URL.revokeObjectURL(imagePreviewUrl.value);
    }
    modalState.value.selectedImage = file;
  }
};

// Add cleanup in onUnmounted
onUnmounted(() => {
  if (imagePreviewUrl.value) {
    window.URL.revokeObjectURL(imagePreviewUrl.value);
  }
});
</script>

<style scoped>
.order-board-container {
  @apply h-screen flex flex-col bg-gray-100 dark:bg-dark-900;
  height: 100vh;
  overflow: hidden;
}

.board-content {
  @apply flex-1 overflow-hidden p-2 sm:p-4;
  height: calc(100vh - 80px);
  /* Adjust for header - more space on mobile */
}

/* Mobile: Horizontal scrollable columns */
.board-columns-mobile {
  @apply h-full overflow-x-auto overflow-y-hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.board-columns-mobile::-webkit-scrollbar {
  height: 8px;
}

.board-columns-mobile::-webkit-scrollbar-track {
  background: transparent;
}

.board-columns-mobile::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 4px;
}

.board-columns-mobile::-webkit-scrollbar-thumb:hover {
  background-color: rgba(156, 163, 175, 0.7);
}

.board-columns-scroll {
  @apply flex gap-3;
  height: 100%;
  min-width: max-content;
}

.board-column-mobile {
  @apply flex flex-col bg-surface-50 dark:bg-surface-800 rounded-lg;
  min-width: 85vw;
  max-width: 85vw;
  width: 85vw;
  height: 100%;
  overflow: hidden;
  flex-shrink: 0;
}

/* Desktop: Grid layout */
.board-columns {
  @apply grid grid-cols-7 gap-4;
  height: 100%;
  min-height: 0;
}

.board-column {
  @apply flex flex-col bg-surface-50 dark:bg-surface-800 rounded-lg;
  min-width: 0;
  height: 100%;
  overflow: hidden;
}

.column-header {
  @apply p-2 sm:p-3 bg-surface-50 dark:bg-surface-800 rounded-t-lg border-b border-surface-200 dark:border-surface-700 flex items-center;
  min-height: 50px;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .column-header {
    min-height: 60px;
  }
}

.column-header h3 {
  @apply text-xs sm:text-sm font-medium text-gray-700 dark:text-dark-100 truncate;
}

.status-dot {
  @apply w-2 h-2 rounded-full mr-2 flex-shrink-0;
}

.count-badge {
  @apply ml-2 px-2 py-0.5 text-xs bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-400 rounded-full flex-shrink-0;
}

.column-content {
  @apply flex-1 overflow-hidden;
  height: calc(100vh - 160px);
  /* Adjust for header + padding on mobile */
  min-height: 0;
}

@media (min-width: 640px) {
  .column-content {
    height: calc(100vh - 180px);
    /* Desktop */
  }
}

.orders-list {
  @apply p-1 sm:p-2;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
}

.orders-container {
  @apply space-y-2 sm:space-y-2;
  flex-grow: 1;
}

.load-more-container {
  @apply mt-4 mb-2 px-2 sticky bottom-0;
  background: linear-gradient(to top, rgb(249 250 251) 60%, transparent);
  padding-top: 1rem;
}

.dark .load-more-container {
  background: linear-gradient(to top, rgb(31 41 55) 60%, transparent);
}

.load-more-btn {
  @apply w-full px-4 py-2.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed border border-primary-200 dark:border-primary-800;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  min-height: 44px;
  touch-action: manipulation;
}

/* Board header styles */
.board-header {
  @apply sticky top-0 z-10;
  flex-shrink: 0;
}

/* Mobile-specific improvements */
@media (max-width: 1023px) {
  .order-board-container {
    height: 100vh;
    height: 100dvh;
    /* Dynamic viewport height for mobile */
  }

  .board-content {
    height: calc(100vh - 120px);
    height: calc(100dvh - 120px);
  }

  .column-content {
    height: calc(100vh - 160px);
    height: calc(100dvh - 160px);
  }

  /* Ensure touch targets are adequate */
  button {
    min-height: 44px;
  }

  /* Better scrolling indicators */
  .board-columns-mobile {
    scroll-snap-type: x mandatory;
  }

  .board-column-mobile {
    scroll-snap-align: start;
  }
}

/* Tablet adjustments */
@media (min-width: 640px) and (max-width: 1023px) {
  .board-column-mobile {
    min-width: 75vw;
    max-width: 75vw;
    width: 75vw;
  }
}

/* Desktop responsive adjustments */
@media (max-width: 1536px) {
  .board-columns {
    @apply gap-3;
  }

  .column-header {
    @apply p-2;
  }
}

@media (max-width: 1280px) {
  .board-header {
    @apply px-4 py-3;
  }

  .board-header h1 {
    @apply text-lg;
  }
}
</style>